import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import { Field, TextInput, Select } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { adminInvitePost } from '../../actions/authAction';
import { ROLES, PERMISSION_MATRIX } from './roles';
import CountryList from '../../static/country-list.json';
import './admin.css';

const EMPTY = { first_name: '', last_name: '', email: '', country: 'India' };

export default function InviteUser() {
  const dispatch = useDispatch();
  const { toast } = useOverlays();
  const activeUser = useSelector((s) => s.auth?.active_user?.data);
  const [form, setForm] = useState(EMPTY);
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  const isSuperAdmin = activeUser?.roles?.includes('super_admin');
  const assignable = ROLES.filter((r) => isSuperAdmin || r.value !== 'super_admin');

  const countries = useMemo(
    () =>
      (Array.isArray(CountryList) ? CountryList : []).map((c) => ({
        value: c.name || c,
        label: c.name || c,
      })),
    []
  );

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const toggleRole = (role) =>
    setRoles((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role]
    );

  const validate = () => {
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'First name is required';
    if (!form.last_name.trim()) next.last_name = 'Last name is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Enter a valid email address';
    if (roles.length === 0) next.roles = 'Pick at least one role';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = useCallback(async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await adminInvitePost(dispatch, { ...form, roles });
      toast(`Invite sent to ${form.email}`, { tone: 'success' });
      setForm(EMPTY);
      setRoles([]);
    } catch {
      toast('Could not send the invite', { tone: 'error' });
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, form, roles, toast]);

  /** A module is reachable if any selected role grants it. */
  const grants = (moduleRoles) => roles.some((r) => moduleRoles.includes(r));

  return (
    <>
      <PageHeader
        section="Admin"
        title="Invite user"
        subtitle="Add someone to the admin panel and choose exactly what they can reach."
      />

      <div className="sv-screen-body">
        <div className="sv-invite">
          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Who</h2>
            <div className="sv-invite__grid">
              <Field label="First name" required error={errors.first_name}>
                <TextInput value={form.first_name} onChange={set('first_name')} invalid={!!errors.first_name} />
              </Field>
              <Field label="Last name" required error={errors.last_name}>
                <TextInput value={form.last_name} onChange={set('last_name')} invalid={!!errors.last_name} />
              </Field>
              <Field label="Email" required error={errors.email} className="sv-span-2">
                <TextInput type="email" value={form.email} onChange={set('email')} invalid={!!errors.email} />
              </Field>
              <Field label="Country" className="sv-span-2">
                <Select options={countries} value={form.country} onChange={set('country')} />
              </Field>
            </div>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">Access</h2>
            <Field label="Roles" required error={errors.roles}>
              <div className="sv-role-grid">
                {assignable.map((role) => (
                  <label className="sv-role-option" key={role.value} title={role.description}>
                    <input
                      type="checkbox"
                      className="sv-checkbox"
                      checked={roles.includes(role.value)}
                      onChange={() => toggleRole(role.value)}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
            </Field>
          </section>

          <section className="sv-card sv-invite__card">
            <h2 className="sv-invite__card-title">What they will reach</h2>
            <table className="sv-matrix">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Access</th>
                </tr>
              </thead>
              <tbody>
                {PERMISSION_MATRIX.map((row) => (
                  <tr key={row.module}>
                    <td>{row.module}</td>
                    <td>
                      <i
                        className={`bx ${grants(row.roles) ? 'bx-check is-yes' : 'bx-minus is-no'}`}
                        aria-label={grants(row.roles) ? 'Can access' : 'No access'}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <div className="sv-invite__foot">
            <Button
              variant="secondary"
              onClick={() => {
                setForm(EMPTY);
                setRoles([]);
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" icon="bx-envelope" onClick={submit} loading={busy}>
              Send invite
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
